import type {
  FrameDiagnostics,
  ProjectileRenderMode,
  RenderDiagnostics,
} from "./core/types";

export interface PerformanceLabVersion {
  id: ProjectileRenderMode;
  number: string;
  label: string;
  headline: string;
  decision: string;
  expected: string;
  prompt: string;
  recorded?: {
    fpsP50: number;
    calls: number;
    geometries: number;
    lights: number;
  };
}

export const PERFORMANCE_LAB_VERSIONS: readonly PerformanceLabVersion[] = [
  {
    id: "baseline",
    number: "01",
    label: "BASELINE",
    headline: "2.3 FPS / 499 calls",
    decision:
      "Every projectile owns four component meshes, unique geometry, and a dynamic point light. It looks richer, but duplicates render work 120 times.",
    expected: "Reproduce ≥450 calls, ≥450 geometries, and ≥120 lights.",
    recorded: {
      fpsP50: 2.3,
      calls: 499,
      geometries: 497,
      lights: 125,
    },
    prompt: `Public reproduction prompt — not a verbatim historical prompt.

Create a deterministic WebGL stress scene with exactly 120 enemy projectiles.
Build every projectile as its own composite group with four visible parts:
core, trail, and two rings. Allocate new geometry for every part and attach one
dynamic point light to every projectile.

Keep the camera, seed, projectile positions, materials, and simulation fixed.
Expose rolling median FPS, p95 FPS, worst frame, draw calls, geometry count,
light count, and scene object count. Do not instance or share geometry.

Acceptance signature: at least 450 draw calls, 450 geometries, and 120 lights.`,
  },
  {
    id: "no-lights",
    number: "02",
    label: "REMOVE LIGHTS",
    headline: "Lights fixed, objects remain",
    decision:
      "Per-projectile lights are removed. The lighting cost falls immediately, but every projectile still owns four unique mesh geometries.",
    expected: "Keep ≥450 calls and geometries while reducing lights to ≤8.",
    prompt: `Public reproduction prompt — not a verbatim historical prompt.

Start from the fixed 120-projectile baseline. Remove every projectile-owned
point light and preserve the five world lights. Do not alter projectile meshes,
geometry allocation, materials, camera, seed, positions, or simulation.

Expose the same rolling frame and render-resource diagnostics.

Acceptance signature: no more than 8 lights while draw calls and geometries
remain above 450. Explain why removing lights alone does not solve object and
geometry overhead.`,
  },
  {
    id: "shared",
    number: "03",
    label: "SHARE GEOMETRY",
    headline: "25 geometries, ~497 calls",
    decision:
      "Projectile variants share component geometry and material resources. Memory pressure falls, but separate mesh objects still require separate draw calls.",
    expected: "Reduce geometries to ≤30 while calls remain ≥450.",
    prompt: `Public reproduction prompt — not a verbatim historical prompt.

Start from the no-lights 120-projectile scene. For each projectile variant,
create one shared core, trail, and ring geometry set and one shared material.
Every projectile may keep its own group and mesh objects, but no component
geometry or material may be cloned per projectile.

Keep the camera, seed, positions, simulation, and diagnostics unchanged.

Acceptance signature: at most 30 geometries and 8 lights while draw calls
remain above 450. State clearly that sharing memory is not draw-call batching.`,
  },
  {
    id: "final",
    number: "04",
    label: "INSTANCE + CAP",
    headline: "59.9 FPS / 21 calls",
    decision:
      "Each composite variant is merged once and rendered as one InstancedMesh. Projectile lights are removed, shaders are prewarmed, and shared VFX are bounded.",
    expected: "Hold calls and geometries to ≤30 and lights to ≤8.",
    recorded: {
      fpsP50: 59.9,
      calls: 21,
      geometries: 21,
      lights: 5,
    },
    prompt: `Public reproduction prompt — not a verbatim historical prompt.

Profile the fixed 120-projectile scene before changing gameplay. Merge each
projectile variant's component geometries once, then render one InstancedMesh
per variant with a dynamic instance matrix. Remove projectile-owned lights,
prewarm the projectile shaders, share hit-effect resources, and enforce a
bounded VFX pool.

Keep the camera, seed, projectile count, positions, materials, simulation, and
public diagnostics fixed. Do not hide projectiles or lower their count.

Acceptance gate: median FPS ≥45, p95 FPS ≥20, draw calls ≤30, geometries ≤30,
lights ≤8, and scene objects ≤50 in the deterministic browser fixture.`,
  },
] as const;

const MYCSBOT_LAB_HOSTS: Readonly<Record<ProjectileRenderMode, string>> = {
  baseline: "abyss-baseline.mycsbot.com",
  "no-lights": "abyss-no-lights.mycsbot.com",
  shared: "abyss-shared.mycsbot.com",
  final: "abyss-final.mycsbot.com",
};

function isMycsbotAbyssHost(hostname: string): boolean {
  return (
    hostname === "abyss.mycsbot.com" ||
    hostname === "play-abyss.mycsbot.com" ||
    Object.values(MYCSBOT_LAB_HOSTS).includes(hostname)
  );
}

export function parsePerformanceLabVersion(
  parameters: URLSearchParams,
): ProjectileRenderMode | null {
  const value = parameters.get("lab");
  return PERFORMANCE_LAB_VERSIONS.some((version) => version.id === value)
    ? (value as ProjectileRenderMode)
    : null;
}

export function performanceLabUrl(
  version: ProjectileRenderMode,
  base = window.location.href,
): string {
  const url = new URL(base);
  if (isMycsbotAbyssHost(url.hostname)) {
    url.protocol = "https:";
    url.hostname = MYCSBOT_LAB_HOSTS[version];
    url.port = "";
    url.pathname = "/";
    url.search = "";
    url.hash = "";
    return url.toString();
  }
  url.search = "";
  url.searchParams.set("lab", version);
  url.searchParams.set("seed", "303");
  return `${url.pathname}${url.search}`;
}

export function fullGameUrl(base = window.location.href): string {
  const url = new URL(base);
  if (!isMycsbotAbyssHost(url.hostname)) return "./";
  return "https://play-abyss.mycsbot.com/";
}

function required<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element as T;
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.append(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
}

export function mountPerformanceLab(options: {
  version: ProjectileRenderMode;
  performance: () => FrameDiagnostics;
  rendering: () => RenderDiagnostics;
  projectileCount: () => number;
}): void {
  const definition = PERFORMANCE_LAB_VERSIONS.find(
    (version) => version.id === options.version,
  );
  if (!definition) throw new Error(`Unknown performance lab version: ${options.version}`);

  document.body.dataset.lab = "true";
  document.body.dataset.labVersion = options.version;
  document.documentElement.lang = "en";
  document.title = `Abyss Relay Performance Lab — ${definition.label}`;
  const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  description?.setAttribute(
    "content",
    "Play the same 120-projectile scene across four rendering stages, from 2.3 to 59.9 FPS.",
  );

  required<HTMLElement>("performance-lab").hidden = false;
  required<HTMLElement>("lab-version-number").textContent = definition.number;
  required<HTMLElement>("lab-version-label").textContent = definition.label;
  required<HTMLElement>("lab-headline").textContent = definition.headline;
  required<HTMLElement>("lab-decision").textContent = definition.decision;
  required<HTMLElement>("lab-expected").textContent = definition.expected;
  required<HTMLElement>("lab-prompt").textContent = definition.prompt;
  required<HTMLAnchorElement>("lab-full-game").href = fullGameUrl();

  const recorded = required<HTMLElement>("lab-recorded");
  if (definition.recorded) {
    recorded.hidden = false;
    required<HTMLElement>("lab-recorded-fps").textContent =
      definition.recorded.fpsP50.toFixed(1);
    required<HTMLElement>("lab-recorded-calls").textContent =
      String(definition.recorded.calls);
    required<HTMLElement>("lab-recorded-geometries").textContent =
      String(definition.recorded.geometries);
    required<HTMLElement>("lab-recorded-lights").textContent =
      String(definition.recorded.lights);
  } else {
    recorded.hidden = true;
  }

  for (const link of document.querySelectorAll<HTMLAnchorElement>("[data-lab-version]")) {
    const id = link.dataset.labVersion as ProjectileRenderMode;
    link.href = performanceLabUrl(id);
    const active = id === options.version;
    link.dataset.active = String(active);
    link.setAttribute("aria-current", active ? "page" : "false");
  }

  const copyButton = required<HTMLButtonElement>("lab-copy-prompt");
  copyButton.addEventListener("click", () => {
    void copyText(definition.prompt).then(() => {
      copyButton.textContent = "COPIED";
      window.setTimeout(() => {
        copyButton.textContent = "COPY REPRODUCTION PROMPT";
      }, 1_600);
    });
  });

  const updateMetrics = (): void => {
    const frame = options.performance();
    const render = options.rendering();
    required<HTMLElement>("lab-live-fps").textContent = frame.fpsP50.toFixed(1);
    required<HTMLElement>("lab-live-p95").textContent = frame.fpsP95.toFixed(1);
    required<HTMLElement>("lab-live-worst").textContent = frame.worstFrameMs.toFixed(1);
    required<HTMLElement>("lab-live-calls").textContent = String(render.calls);
    required<HTMLElement>("lab-live-geometries").textContent = String(render.geometries);
    required<HTMLElement>("lab-live-lights").textContent = String(render.lights);
    required<HTMLElement>("lab-live-objects").textContent = String(render.objects);
    required<HTMLElement>("lab-live-projectiles").textContent = String(
      options.projectileCount(),
    );
  };

  updateMetrics();
  window.setInterval(updateMetrics, 400);
}
